import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useForm } from 'react-hook-form';
import { Common } from '../../utils/common';
import { getFilterValues } from './misc';
import _ from 'lodash';

const timeframeOptionLabelText = {
  DAILY: 'Daily',
  PRIOR_WEEK: 'Prior Week (Delivered on Mondays)',
  MONTH_TO_DATE: 'Month To Date',
  PRIOR_MONTH: 'Prior Month (Sent on the 1st of each month)',
};

// Modal form for scheduling reports
function ReportScheduler({
  controllerName,
  filters,
  timeframeOptions = ['DAILY', 'PRIOR_WEEK', 'MONTH_TO_DATE', 'PRIOR_MONTH'],
}) {
  const [error, setError] = useState(null);
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({ defaultValues: { timeframe: timeframeOptions[0] } });

  async function scheduleReport(formData) {
    setError(null);

    let emails = [];
    for (let i = 1; i <= 5; i++) {
      const email = formData[`email${i}`];
      if (email) {
        emails.push(email);
      }
    }

    const body = {
      emails,
      report: controllerName,
      customFilters: _.omit(getFilterValues(filters), ['start_date', 'end_date']),
      name: formData.report_name,
      timezone: formData.timezone,
      timeframe: formData.timeframe,
    };

    try {
      const res = await fetch('/user_report_schedule', {
        headers: {
          'X-CSRF-Token': document.querySelector('meta[name=csrf-token]').content,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.ok) {
        // Closes modal
        document.querySelector('[data-dismiss]').click();
        // Clears form fields
        reset();
        Common.notify('New Scheduled Report created', 'success');
      } else {
        setError(data.message);
      }
    } catch (error) {
      console.error(error);
      setError('Failed to schedule report');
    }
  }

  return (
    <>
      <a href='#' title='Schedule Report' data-toggle='modal' data-target='#scheduler'>
        <span className='fa fa-clock-o'></span>
      </a>
      <div className='modal' tabIndex='-1' role='dialog' id='scheduler'>
        <div className='modal-dialog'>
          <div className='modal-content'>
            <div className='modal-header'>
              <button type='button' className='close' data-dismiss='modal' aria-hidden='true'>
                x
              </button>
              <div className='form-group text-center'>
                <h3>Report Scheduler</h3>
              </div>
            </div>
            <div className='modal-body'>
              {error && (
                <div className='alert alert-danger alert-dismissible' role='alert'>
                  <button
                    onClick={() => setError(null)}
                    type='button'
                    className='close'
                    aria-label='Close'
                  >
                    <span aria-hidden='true'>&times;</span>
                  </button>
                  {error}
                </div>
              )}
              <form className='form-horizontal' onSubmit={handleSubmit(scheduleReport)}>
                <div className={`form-group ${errors.report_name ? 'has-error' : ''}`}>
                  <label htmlFor='report_name' className='col-sm-3 control-label'>
                    Give report a name
                  </label>
                  <div className='col-sm-9'>
                    <input
                      {...register('report_name', { required: true, maxLength: 50 })}
                      type='text'
                      id='report_name'
                      className='form-control'
                    />
                    {errors.report_name?.type === 'required' ? (
                      <span className='help-block'>Must not be blank</span>
                    ) : errors.report_name?.type === 'maxLength' ? (
                      <span className='help-block'>Report name cannot exceed 50 characters</span>
                    ) : null}
                  </div>
                </div>
                <div className='form-group'>
                  <label htmlFor='timezone' className='col-sm-3 control-label'>
                    Timezone
                  </label>
                  <div className='col-sm-9'>
                    <select {...register('timezone')} className='form-control' id='timezone'>
                      <option value='US/Pacific'>US/Pacific</option>
                      <option value='US/Mountain'>US/Mountain</option>
                      <option value='US/Central'>US/Central</option>
                      <option value='US/Eastern'>US/Eastern</option>
                    </select>
                  </div>
                </div>
                <div className={`form-group ${errors.timeframe ? 'has-error' : ''}`}>
                  <label htmlFor='timeframe' className='col-sm-3 control-label'>
                    Report Timeframe
                  </label>
                  <div className='col-sm-9'>
                    {errors.timeframe && (
                      <span className='help-block'>Timeframe must be selected</span>
                    )}
                    {timeframeOptions.map((option) => {
                      return (
                        <div className='radio' key={option}>
                          <label>
                            <input
                              {...register('timeframe', { required: true })}
                              type='radio'
                              value={option}
                            ></input>
                            {timeframeOptionLabelText[option]}
                          </label>
                        </div>
                      );
                    })}
                  </div>
                </div>
                <div
                  className={`form-group ${
                    errors.email1 ||
                    errors.email2 ||
                    errors.email3 ||
                    errors.email4 ||
                    errors.email5
                      ? 'has-error'
                      : ''
                  }`}
                >
                  <label className='col-sm-3 control-label'>Recipients</label>
                  <div className='col-sm-9 report-scheduler-email-inputs'>
                    {errors.email1 ||
                    errors.email2 ||
                    errors.email3 ||
                    errors.email4 ||
                    errors.email5 ? (
                      <span className='help-block'>Please enter valid email addresses</span>
                    ) : (
                      <span className='help-block'>Maximum of 5 emails addresses</span>
                    )}
                    <input
                      {...register('email1', {
                        required: true,
                        pattern: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
                      })}
                      type='email'
                      className='form-control'
                      id='email1'
                      placeholder='name@example.com'
                    />
                    <input
                      {...register('email2', {
                        pattern: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
                      })}
                      type='email'
                      className='form-control'
                      id='email2'
                      placeholder='name@example.com'
                    />
                    <input
                      {...register('email3', {
                        pattern: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
                      })}
                      type='email'
                      className='form-control'
                      id='email3'
                      placeholder='name@example.com'
                    />
                    <input
                      {...register('email4', {
                        pattern: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
                      })}
                      type='email'
                      className='form-control'
                      id='email4'
                      placeholder='name@example.com'
                    />
                    <input
                      {...register('email5', {
                        pattern: /^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$/,
                      })}
                      type='email'
                      className='form-control'
                      id='email5'
                      placeholder='name@example.com'
                    />
                  </div>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <button type='button' className='btn btn-default' data-dismiss='modal'>
                    Close
                  </button>
                  <button type='submit' className='btn btn-primary' disabled={isSubmitting}>
                    Schedule Report
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

ReportScheduler.propTypes = {
  filters: PropTypes.object.isRequired,
  controllerName: PropTypes.string.isRequired,
  timeframeOptions: PropTypes.arrayOf(PropTypes.string),
};

export default ReportScheduler;
